import { useEffect, useState } from "react";
import { t } from "ttag";

import { CopyButton } from "metabase/common/components/CopyButton";
import { Button, Flex, Group, Modal, Paper, Stack, Text } from "metabase/ui";

import Styles from "./AccessLinksModal.module.css";
import { type ExternalProxyRule, getProxyConfigAPI } from "./api";
import { useNestedModalEscape } from "./useNestedModalEscape";

const PROXY_BASE_URL = "https://metabase-proxy.getjerry.com/report";
type ExternalProxyParamValue = NonNullable<ExternalProxyRule["params"]>[string];

interface AccessLinksModalProps {
  opened: boolean;
  onClose: () => void;
  resourceId: number | string;
  resourceType: "dashboard" | "question";
}

const formatParamValue = (value: ExternalProxyParamValue) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return value ?? "";
};

const getParamEntries = (params?: ExternalProxyRule["params"]) =>
  Object.entries(params ?? {}).map(([key, value]) => ({
    key,
    value: formatParamValue(value) || "-",
  }));

const getAuthorizedUsers = (email?: string) =>
  email
    ?.split(",")
    .map((value) => value.trim())
    .filter(Boolean) ?? [];

export const AccessLinksModal = ({
  opened,
  onClose,
  resourceId,
  resourceType,
}: AccessLinksModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [accessRules, setAccessRules] = useState<ExternalProxyRule[]>([]);

  const modalContentRef = useNestedModalEscape({ opened, onClose });

  useEffect(() => {
    if (!opened || !resourceId) {
      return;
    }

    let isCancelled = false;
    const loadAccessRules = async () => {
      setIsLoading(true);
      try {
        const responseData = await getProxyConfigAPI({
          resource_id: resourceId,
          resource_type: resourceType,
        });
        if (!isCancelled) {
          setAccessRules(
            (responseData.rules || []).filter((rule) => rule.is_publish),
          );
        }
      } catch {
        if (!isCancelled) {
          setAccessRules([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadAccessRules();

    return () => {
      isCancelled = true;
    };
  }, [opened, resourceId, resourceType]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      closeOnEscape={false}
      title={t`External Access Links`}
      size="xl"
    >
      <div ref={modalContentRef}>
        <Stack gap="md">
          <Text>
            {t`The following links are now active for external users. Share the appropriate link with each user.`}
          </Text>

          {isLoading ? (
            <Text>{t`Loading links...`}</Text>
          ) : accessRules.length === 0 ? (
            <Text>{t`No published external access links yet.`}</Text>
          ) : (
            <Stack gap="md">
              {accessRules.map((rule, index) => {
                const link = `${PROXY_BASE_URL}/${rule.id}`;
                const paramEntries = getParamEntries(rule.params);
                const authorizedUsers = getAuthorizedUsers(rule.email);

                return (
                  <Paper key={rule.id ?? index} withBorder p="lg" radius="md">
                    <Stack gap="md">
                      <Flex
                        align="flex-start"
                        justify="space-between"
                        gap="md"
                        wrap="wrap"
                      >
                        <Stack gap={4} className={Styles.headerBlock}>
                          <Text fw={700}>{t`Access Link ${index + 1}`}</Text>
                          <Text size="sm" c="text-medium">
                            {t`Use the actions on the right to open or copy this link.`}
                          </Text>
                        </Stack>

                        <Group
                          gap="sm"
                          justify="flex-end"
                          wrap="wrap"
                          className={Styles.actions}
                        >
                          <Button
                            component="a"
                            href={link}
                            rel="noreferrer"
                            target="_blank"
                            variant="default"
                          >
                            {t`Open Link`}
                          </Button>
                          <CopyButton
                            value={link}
                            target={
                              <Button variant="filled">{t`Copy Link`}</Button>
                            }
                          />
                        </Group>
                      </Flex>

                      <div>
                        <Text
                          className={Styles.sectionLabel}
                        >{t`Access URL`}</Text>
                        <Text className={Styles.linkValue}>{link}</Text>
                      </div>

                      <div>
                        <Text
                          className={Styles.sectionLabel}
                        >{t`Applies when`}</Text>
                        {paramEntries.length > 0 ? (
                          <div className={Styles.tokenGrid}>
                            {paramEntries.map((entry) => (
                              <div
                                key={entry.key}
                                className={Styles.paramToken}
                              >
                                <Text className={Styles.tokenLabel}>
                                  {entry.key}
                                </Text>
                                <Text className={Styles.tokenValue}>
                                  {entry.value}
                                </Text>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <Text
                            className={Styles.emptyState}
                          >{t`Default view`}</Text>
                        )}
                      </div>

                      <div>
                        <Text className={Styles.sectionLabel}>
                          {t`Authorized Users`}
                        </Text>
                        {authorizedUsers.length > 0 ? (
                          <div className={Styles.userGrid}>
                            {authorizedUsers.map((email) => (
                              <div key={email} className={Styles.userPill}>
                                <Text className={Styles.userText}>{email}</Text>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <Text className={Styles.emptyState}>-</Text>
                        )}
                      </div>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}

          <Group justify="flex-end">
            <Button variant="filled" onClick={onClose}>
              {t`OK`}
            </Button>
          </Group>
        </Stack>
      </div>
    </Modal>
  );
};
